<template>
  <div class="computed-fields-editor">
    <EmptyState
      v-if="!localFields.length"
      title="Nog geen berekende velden"
      description="Voeg berekende velden toe die automatisch worden uitgerekend op basis van andere opties"
    >
      <Btn variant="secondary" @click="addField" icon="plus">Eerste veld toevoegen</Btn>
    </EmptyState>

    <div v-else class="rules-list">
      <div v-for="(field, idx) in localFields" :key="field.id" class="rule-card">
        <div class="rule-header">
          <div class="rule-info">
            <InplaceEdit
              :id="`field-label-${idx}`"
              v-model="field.label"
              placeholder="Klik om naam in te voeren..."
              :autoFocus="field.id === newlyAddedFieldId"
              class="field-label-input"
            />
          </div>
          <Btn
            icon="trash"
            variant="outlined"
            size="lg"
            @click="removeField(idx)"
            title="Verwijder veld"
          />
        </div>

        <div class="rule-body">
          <div class="rule-section formula-section">
            <FormulaEditor
              :id="`field-formula-${idx}`"
              label="Formule"
              v-model="field.formula"
              :custom-fields="formulaFields"
              :show-conditional-operators="false"
              compact
            />
            <p class="formula-help">
              Gebruik <code>{'{'}option_id{'}'}</code> om optie-waarden te gebruiken. 
              Beschikbare operators: <code>+ - * / ( )</code>
            </p>
          </div>

          <!-- Preview -->
          <div v-if="field.formula" class="rule-preview">
            <span class="preview-label">Voorbeeld berekening:</span>
            <code class="preview-formula">{{ formatFormulaPreview(field.formula) }}</code>
          </div>
        </div>
      </div>

      <Btn variant="dashed" @click="addField" icon="plus" class="add-rule-btn">
        Berekend veld toevoegen
      </Btn>
    </div>
  </div>
</template>

<script>
  import { ref, computed, watch, nextTick } from 'vue';
  import FormulaEditor from '@/components/inputs/FormulaEditor.vue';
  import Btn from '@/components/partials/Btn.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import InplaceEdit from '@/components/partials/InplaceEdit.vue';
  import { uid } from 'uid';

  export default {
    name: 'ComputedFieldsEditor',

    components: {
      FormulaEditor,
      Btn,
      EmptyState,
      InplaceEdit,
    },

    props: {
      modelValue: {
        type: Array,
        default: () => [],
      },
      productOptions: {
        type: Array,
        default: () => [],
      },
    },

    emits: ['update:modelValue'],

    setup(props, { emit }) {
      // Local copy of fields
      const localFields = ref([...props.modelValue]);
      // Track newly added field for auto-focus
      const newlyAddedFieldId = ref(null);
      // Guard against watcher feedback loop between prop and local copy
      const isUpdatingFromExternal = ref(false);

      // Watch for external changes
      watch(
        () => props.modelValue,
        (newValue) => {
          const nextFields = [...(newValue || [])];
          if (JSON.stringify(nextFields) === JSON.stringify(localFields.value)) return;
          isUpdatingFromExternal.value = true;
          localFields.value = nextFields;
          nextTick(() => {
            isUpdatingFromExternal.value = false;
          });
        },
        { deep: true }
      );

      // Emit changes
      watch(
        localFields,
        (newValue) => {
          if (isUpdatingFromExternal.value) return;
          emit('update:modelValue', newValue);
        },
        { deep: true }
      );

      // Formula fields for FormulaEditor (product options)
      const formulaFields = computed(() => {
        const fields = props.productOptions.map((option) => ({
          id: option.id,
          label: option.label,
          type: option.type === 'number' ? 'number' : 'single-choice',
          choices:
            option.type === 'single-choice' || option.type === 'multi-choice'
              ? option.choiceConfig?.choices
              : null,
        }));

        return fields;
      });

      // Add new field
      const addField = () => {
        const fieldId = `computed_${uid()}`;
        const newField = {
          id: fieldId,
          label: '',
          formula: '',
        };
        localFields.value.push(newField);
        
        // Set as newly added for auto-focus
        newlyAddedFieldId.value = fieldId;
        // Clear after next tick so it doesn't re-focus on re-render
        nextTick(() => {
          setTimeout(() => {
            newlyAddedFieldId.value = null;
          }, 100);
        });
      };

      // Remove field
      const removeField = (index) => {
        localFields.value.splice(index, 1);
      };

      // Format formula for preview (replace IDs with labels)
      const formatFormulaPreview = (formula) => {
        if (!formula) return '';

        let preview = formula;
        props.productOptions.forEach((option) => {
          const regex = new RegExp(`\\{${option.id}\\}`, 'g');
          preview = preview.replace(regex, `[${option.label}]`);
        });

        return preview;
      };

      return {
        localFields,
        formulaFields,
        addField,
        removeField,
        formatFormulaPreview,
        newlyAddedFieldId,
      };
    },
  };
</script>

<style scoped lang="scss">
  .computed-fields-editor {
    .rules-list {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .rule-card {
      @include rule-card;

      .rule-header {
        @include rule-card-header;

        .rule-info {
          flex: 1;
        }

        .field-label-input {
          max-width: 300px;
        }
      }

      .rule-body {
        @include rule-card-body;
      }

      .rule-section {
        @include rule-card-section;
      }

      .formula-section {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .formula-help {
        font-size: 0.75rem;
        color: var(--slate-500);
        margin: 0;

        code {
          background: var(--slate-200);
          padding: 0.125rem 0.25rem;
          border-radius: 0.25rem;
          font-family: 'JetBrains Mono', monospace;
        }
      }

      .rule-preview {
        @include rule-card-preview;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .preview-label {
          font-size: 0.75rem;
          color: var(--slate-600);
          font-weight: 500;
        }

        .preview-formula {
          font-size: 0.75rem;
          font-family: 'JetBrains Mono', monospace;
          color: var(--brand-600);
          background: white;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          border: 1px solid var(--slate-200);
        }
      }
    }

    .add-rule-btn {
      margin-top: 0.5rem;
    }
  }
</style>
